import { useSpring } from '@react-spring/web';
import { useRef, useLayoutEffect } from 'react';

interface UseAnimatedNumberOptions {
  /** Animation duration in milliseconds (default: 1000) */
  duration?: number;
  /** Spring configuration preset or custom config */
  config?: 'default' | 'gentle' | 'wobbly' | 'stiff' | 'slow' | 'molasses' | object;
  /** Whether to enable animation (default: true) */
  enabled?: boolean;
}

/**
 * Custom hook for animating number changes with react-spring
 * Provides smooth transitions when number values change
 *
 * @param currentValue - The current number to animate to
 * @param options - Configuration options for the animation
 * @returns Spring animation object with animated number
 */
export const useAnimatedNumber = (
  currentValue: any, // return number or N/A
  options: UseAnimatedNumberOptions = {},
) => {
  const { duration = 1000, config = 'default', enabled = true } = options;

  // Store previous value using useRef to create smooth transitions
  const prevValueRef = useRef<number>(0);
  const previousValue = prevValueRef.current;

  // Spring configuration presets
  const springConfigs = {
    default: { duration },
    gentle: { tension: 120, friction: 14 },
    wobbly: { tension: 180, friction: 12 },
    stiff: { tension: 210, friction: 20 },
    slow: { tension: 280, friction: 60 },
    molasses: { tension: 280, friction: 120 },
  };

  // Get spring config based on option
  const getSpringConfig = () => {
    if (typeof config === 'object') {
      return config;
    }
    return springConfigs[config] || springConfigs.default;
  };

  // Create spring animation
  const spring = useSpring({
    from: { number: enabled ? previousValue : currentValue },
    to: { number: currentValue },
    config: getSpringConfig(),
    immediate: !enabled, // Skip animation if disabled
  });

  // Update ref after animation is triggered
  useLayoutEffect(() => {
    if (enabled) {
      prevValueRef.current = currentValue;
    }
  }, [currentValue, enabled]);

  return spring;
};

export default useAnimatedNumber;
